import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Avatar, Stack, IconButton, TextField,
  CircularProgress, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import { Send, ArrowBack, MailOutline } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import { useDirectMessages } from '../hooks/useDirectMessages';
import { messageService, DmMessage, DmConversationSummary, StoryRef } from '../services/messageService';
import { photoUrl } from '../services/postService';
import { isVideoKey, videoPosterKey } from '../utils/media';
import { formatDistanceToNow } from 'date-fns';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function timeAgo(iso: string) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
}

/**
 * The story a message replied to, shown above the bubble. Stories are deleted within a day,
 * so the thumbnail may 404 — then it degrades to a plain "story has expired" tile.
 */
const QuotedStory: React.FC<{ story: StoryRef; mine: boolean; otherName: string }> = ({ story, mine, otherName }) => {
  const [gone, setGone] = useState(false);
  const thumbKey = isVideoKey(story.mediaKey) ? videoPosterKey(story.mediaKey) : story.mediaKey;
  // "mine" is whether *I* sent this message; a reply is always to the other participant's story.
  const label = story.reaction
    ? (mine ? `You reacted to ${otherName.split(' ')[0]}'s story` : `Reacted to your story`)
    : (mine ? `You replied to ${otherName.split(' ')[0]}'s story` : `Replied to your story`);
  return (
    <Stack spacing={0.5} sx={{ alignItems: mine ? 'flex-end' : 'flex-start', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ width: 96, height: 140, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover', borderLeft: '3px solid', borderColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {gone || !story.mediaKey ? (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>Story has expired</Typography>
        ) : (
          <img src={photoUrl(thumbKey)} alt="" onError={() => setGone(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
      </Box>
      {story.caption && !gone && (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          “{story.caption}”
        </Typography>
      )}
    </Stack>
  );
};

/**
 * 1:1 direct messages. Not gated on buddy status — messaging works before connecting, gated
 * only on a lighter signal (either party follows the other, or they're accepted buddies).
 * This app has no realtime transport, so a thread refreshes on send/open, not live-pushed —
 * near-live via the same polling ceiling every other "live-ish" surface here uses.
 */
const Messages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const { conversations, loading: inboxLoading, refresh: refreshInbox } = useDirectMessages(userId);

  const [searchParams, setSearchParams] = useSearchParams();
  const openWithUserId = searchParams.get('with');
  const [activeThreadUserId, setActiveThreadUserId] = useState<string | null>(openWithUserId);

  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (openWithUserId) setActiveThreadUserId(openWithUserId);
  }, [openWithUserId]);

  const loadThread = useCallback(async (otherUserId: string) => {
    setThreadLoading(true);
    setThreadError(null);
    try {
      const thread = await messageService.getThread(otherUserId);
      setMessages(thread.messages);
      refreshInbox();
    } catch (err) {
      setThreadError(err instanceof Error && err.message ? err.message : 'Failed to load conversation');
    } finally {
      setThreadLoading(false);
    }
  }, [refreshInbox]);

  useEffect(() => {
    if (activeThreadUserId) loadThread(activeThreadUserId);
  }, [activeThreadUserId, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openThread = (otherUserId: string) => {
    setActiveThreadUserId(otherUserId);
    setSearchParams({ with: otherUserId });
  };

  const closeThread = () => {
    setActiveThreadUserId(null);
    setSearchParams({});
  };

  const handleSend = async () => {
    if (!draft.trim() || !activeThreadUserId || sending) return;
    setSending(true);
    try {
      const message = await messageService.sendMessage(activeThreadUserId, draft.trim());
      setMessages(prev => [...prev, message]);
      setDraft('');
      refreshInbox();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="message other divers"
          description="Send a direct message to a diver you follow or dive with — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  const activeSummary = conversations.find(c => c.otherUserId === activeThreadUserId);
  const showThread = !!activeThreadUserId && (!isMobile || activeThreadUserId);
  const showInboxList = !isMobile || !activeThreadUserId;

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 2 }}>Messages</Typography>

        <Paper elevation={2} sx={{ display: 'flex', height: 560, overflow: 'hidden' }}>
          {showInboxList && (
            <Box sx={{ width: isMobile ? '100%' : 300, borderRight: isMobile ? 'none' : '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
              {inboxLoading && conversations.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!inboxLoading && conversations.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                  <MailOutline sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No conversations yet. Start one from a diver's profile.
                  </Typography>
                </Box>
              )}
              <Stack divider={<Divider />}>
                {conversations.map((c: DmConversationSummary) => (
                  <Box
                    key={c.conversationId}
                    sx={{
                      p: 1.5, cursor: 'pointer', display: 'flex', gap: 1.5, alignItems: 'center',
                      bgcolor: c.otherUserId === activeThreadUserId ? 'action.selected' : undefined,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => openThread(c.otherUserId)}
                  >
                    <Avatar src={c.otherProfilePhotoUrl ?? undefined} sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700 }}>
                      {!c.otherProfilePhotoUrl && (initials(c.otherDisplayName) || '?')}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: c.hasUnread ? 700 : 600 }}>
                        {c.otherDisplayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.lastMessagePreview}
                      </Typography>
                    </Box>
                    {c.hasUnread && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {showThread && (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                {isMobile && (
                  <IconButton size="small" onClick={closeThread}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                )}
                <Avatar
                  src={activeSummary?.otherProfilePhotoUrl ?? undefined}
                  sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                  onClick={() => activeThreadUserId && navigate(`/u/${activeThreadUserId}`)}
                >
                  {!activeSummary?.otherProfilePhotoUrl && (initials(activeSummary?.otherDisplayName ?? '') || '?')}
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {activeSummary?.otherDisplayName ?? 'Conversation'}
                </Typography>
              </Stack>

              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
                {threadLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                {threadError && <Typography color="error" variant="body2">{threadError}</Typography>}
                {!threadLoading && !threadError && (
                  <Stack spacing={1}>
                    {messages.map(m => (
                      <Box
                        key={m.messageId}
                        sx={{
                          alignSelf: m.senderUserId === userId ? 'flex-end' : 'flex-start',
                          maxWidth: '75%',
                        }}
                      >
                        {m.story && (
                          <QuotedStory story={m.story} mine={m.senderUserId === userId} otherName={activeSummary?.otherDisplayName ?? 'their'} />
                        )}
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1,
                            bgcolor: m.senderUserId === userId ? 'primary.main' : 'background.default',
                            color: m.senderUserId === userId ? 'primary.contrastText' : 'text.primary',
                            ...(m.story?.reaction && m.text === m.story.reaction ? { bgcolor: 'transparent', border: 'none', p: 0 } : {}),
                          }}
                        >
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontSize: m.story?.reaction && m.text === m.story.reaction ? 32 : undefined, lineHeight: m.story?.reaction && m.text === m.story.reaction ? 1.2 : undefined }}>{m.text}</Typography>
                        </Paper>
                        <Typography variant="caption" color="text.secondary">{timeAgo(m.createdAt)}</Typography>
                      </Box>
                    ))}
                    <div ref={bottomRef} />
                  </Stack>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type a message…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                  autoComplete="off"
                />
                <IconButton color="primary" onClick={handleSend} disabled={!draft.trim() || sending}>
                  {sending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                </IconButton>
              </Stack>
            </Box>
          )}

          {!isMobile && !activeThreadUserId && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">Select a conversation</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Messages;
